/**
 * Pure JavaScript parser for Meshpit scene-extension module calls in OpenSCAD source.
 *
 * This extracts lights, PiP cameras, and scene overrides directly from the raw SCAD
 * source string, WITHOUT running the WASM compiler. This lets the Three.js scene
 * update immediately when only these declarations are edited, skipping the full
 * compile/detect pipeline entirely.
 *
 * Supports both positional and named-parameter call syntax, e.g.:
 *   light_point([1,1,1], [0,0,10], 1.0, 50, 2)
 *   light_point(color=[1,1,1], position=[0,0,10], intensity=1.0)
 *
 * The sentinel value -1 (OpenSCAD convention meaning "not specified") is
 * handled the same way as in openSCAD.ts: -1 → undefined for optional fields.
 *
 * IMPORTANT: This parser operates on raw source (before preprocessing) and is
 * deliberately lenient. It is not a full AST parser — it skips calls it cannot
 * confidently parse rather than throwing. The WASM detection pass remains the
 * source of truth; this JS parser only provides an immediate preview update
 * before WASM finishes.
 */

import type {
  SceneLight,
  SceneOverrides,
  SceneGrid,
  PiPCamera,
  CameraPathWaypoint,
  SvgOverlay,
  AnnotationDef,
  LightLFO,
  LightColorLFO,
  LightLFOBindings,
  LFOShape,
  MotionDef,
} from '@/worker/types';
import { stripScadComments } from './scad-normalizer';

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type SceneExtensions = {
  lights: SceneLight[];
  pipCameras: PiPCamera[];
  /** Ordered intro camera path defined by camera_path() calls. */
  cameraPath: CameraPathWaypoint[];
  svgOverlays: SvgOverlay[];
  sceneOverrides: SceneOverrides;
  /** Annotations detected in the source (no geometry association — WASM is authoritative for that). */
  annotations: AnnotationDef[];
  /**
   * True when at least one motion() wrapper is present in the source.
   * Used to decide whether to show the animation pause toolbar button.
   * Geometry association is authoritative from WASM (via motionMap in preprocessor).
   */
  hasMotion: boolean;
};

/**
 * Parse all scene-extension declarations from raw SCAD source.
 * Returns lights, PiP cameras, and scene overrides extracted via JS regex
 * — no WASM involved.
 */
export function parseSceneExtensions(rawCode: string): SceneExtensions {
  // Strip comments so comment text doesn't confuse the parser
  const code = stripScadComments(rawCode);

  const lights: SceneLight[] = [];
  const pipCameras: PiPCamera[] = [];
  const cameraPath: CameraPathWaypoint[] = [];
  const svgOverlays: SvgOverlay[] = [];
  const sceneOverrides: SceneOverrides = {};
  const annotations: AnnotationDef[] = [];
  let hasMotion = false;

  // Find all top-level module calls for the modules we care about and parse them.
  for (const call of iterateModuleCalls(code)) {
    switch (call.name) {
      case 'light_ambient': {
        const light = parseLightAmbient(call.args);
        if (light) lights.push(light);
        break;
      }
      case 'light_directional': {
        const light = parseLightDirectional(call.args);
        if (light) lights.push(light);
        break;
      }
      case 'light_point': {
        const light = parseLightPoint(call.args);
        if (light) lights.push(light);
        break;
      }
      case 'light_spot': {
        const light = parseLightSpot(call.args);
        if (light) lights.push(light);
        break;
      }
      case 'scene_material': {
        const mat = parseSceneMaterial(call.args);
        if (mat)
          sceneOverrides.material = {
            ...(sceneOverrides.material ?? {}),
            ...mat,
          };
        break;
      }
      case 'scene_default_lights': {
        const dl = parseSceneDefaultLights(call.args);
        if (dl)
          sceneOverrides.defaultLights = {
            ...(sceneOverrides.defaultLights ?? {}),
            ...dl,
          };
        break;
      }
      case 'scene_shadows': {
        const sh = parseSceneShadows(call.args);
        if (sh)
          sceneOverrides.shadows = { ...(sceneOverrides.shadows ?? {}), ...sh };
        break;
      }
      case 'scene_bloom': {
        const bl = parseSceneBloom(call.args);
        if (bl)
          sceneOverrides.bloom = { ...(sceneOverrides.bloom ?? {}), ...bl };
        break;
      }
      case 'scene_ssao': {
        const ao = parseSceneSsao(call.args);
        if (ao) sceneOverrides.ssao = { ...(sceneOverrides.ssao ?? {}), ...ao };
        break;
      }
      case 'scene_background': {
        const bg = parseSceneBackground(call.args);
        if (bg !== undefined) sceneOverrides.backgroundColor = bg;
        break;
      }
      case 'scene_grid': {
        const grid = parseSceneGrid(call.args);
        if (grid)
          sceneOverrides.grids = [...(sceneOverrides.grids ?? []), grid];
        break;
      }
      case 'scene_camera': {
        const cam = parseSceneCamera(call.args);
        if (cam)
          sceneOverrides.camera = {
            ...(sceneOverrides.camera ?? {}),
            ...cam,
          };
        break;
      }
      case 'camera_pip': {
        const pip = parseCameraPip(call.args);
        // x=-1, y=-1 is the sentinel for a parameter-panel PiP (no overlay).
        // Negative pixel values like x=-80 are valid right-anchored positions.
        if (pip && !(pip.x === -1 && pip.y === -1) && pipCameras.length < 4)
          pipCameras.push(pip);
        break;
      }
      case 'camera_path': {
        const waypoint = parseCameraPath(call.args);
        if (waypoint) cameraPath.push(waypoint);
        break;
      }
      case 'svg_overlay': {
        const overlay = parseSvgOverlay(call.args);
        if (overlay) svgOverlays.push(overlay);
        break;
      }
      case 'annotation': {
        const ann = parseAnnotation(call.args);
        if (ann) annotations.push(ann);
        break;
      }
      case 'motion': {
        // We only need to know that motion() is present so the pause toolbar
        // can be shown. Geometry association is handled by the preprocessor
        // (motionMap) and the WASM detect pipeline.
        hasMotion = true;
        break;
      }
    }
  }

  return {
    lights,
    pipCameras,
    cameraPath,
    svgOverlays,
    sceneOverrides,
    annotations,
    hasMotion,
  };
}

// ---------------------------------------------------------------------------
// Module call iterator
// ---------------------------------------------------------------------------

type ModuleCall = { name: string; args: string };

const KNOWN_MODULES = new Set([
  'light_ambient',
  'light_directional',
  'light_point',
  'light_spot',
  'scene_material',
  'scene_default_lights',
  'scene_shadows',
  'scene_bloom',
  'scene_ssao',
  'scene_background',
  'scene_grid',
  'scene_camera',
  'camera_pip',
  'camera_path',
  'svg_overlay',
  'annotation',
  'motion',
]);

/**
 * Iterate over all top-level calls of known modules in the code.
 * Yields { name, args } where args is the raw content between the outermost parens.
 */
function* iterateModuleCalls(code: string): Generator<ModuleCall> {
  // Match module name followed by '(' — not preceded by identifier chars (avoids e.g. my_light_point)
  const re = /(?<![a-zA-Z_$0-9])([a-zA-Z_][a-zA-Z0-9_]*)\s*\(/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(code)) !== null) {
    const name = m[1];
    if (!KNOWN_MODULES.has(name)) continue;
    // m.index is start of name; m.index + m[0].length - 1 is the '('
    const openParen = m.index + m[0].length - 1;
    const closeParen = findBalancedParen(code, openParen);
    if (closeParen === -1) continue;
    const args = code.slice(openParen + 1, closeParen - 1);
    yield { name, args };
  }
}

/**
 * Find the index after the closing ')' that matches the '(' at openIdx.
 * Returns -1 if unbalanced.
 */
function findBalancedParen(code: string, openIdx: number): number {
  let depth = 0;
  let inStr = false;
  for (let i = openIdx; i < code.length; i++) {
    const ch = code[i];
    if (ch === '"' && (i === 0 || code[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '(') depth++;
    else if (ch === ')') {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Argument parsing helpers
// ---------------------------------------------------------------------------

/**
 * Split a module argument list string into individual argument strings,
 * respecting nested brackets/parens and string literals.
 *
 * e.g. "color=[1,1,1], position=[0,0,10], intensity=1.0"
 *   → ["color=[1,1,1]", " position=[0,0,10]", " intensity=1.0"]
 */
function splitArgs(args: string): string[] {
  const result: string[] = [];
  let depth = 0;
  let inStr = false;
  let start = 0;

  for (let i = 0; i < args.length; i++) {
    const ch = args[i];
    if (ch === '"' && (i === 0 || args[i - 1] !== '\\')) {
      inStr = !inStr;
      continue;
    }
    if (inStr) continue;
    if (ch === '(' || ch === '[') depth++;
    else if (ch === ')' || ch === ']') depth--;
    else if (ch === ',' && depth === 0) {
      result.push(args.slice(start, i));
      start = i + 1;
    }
  }
  result.push(args.slice(start));
  return result;
}

/**
 * Parse an argument list into a map of named parameter values.
 * Also tracks positional order so positional-only calls can be resolved.
 *
 * Returns { byName: Map<string, string>, positional: string[] }
 *
 * Each value string is the raw text (trimmed), e.g. "[1, 0, 0]" or "1.0".
 */
type ParsedArgs = { byName: Map<string, string>; positional: string[] };

function parseArgs(args: string): ParsedArgs {
  const parts = splitArgs(args);
  const byName = new Map<string, string>();
  const positional: string[] = [];

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    const eqIdx = trimmed.indexOf('=');
    if (eqIdx !== -1) {
      // Check it's really a named param (not inside brackets)
      const before = trimmed.slice(0, eqIdx);
      // Only treat as named param if everything before '=' is an identifier
      if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(before.trim())) {
        byName.set(before.trim(), trimmed.slice(eqIdx + 1).trim());
        positional.push(trimmed.slice(eqIdx + 1).trim()); // also track position
        continue;
      }
    }
    positional.push(trimmed);
  }

  return { byName, positional };
}

/**
 * Get a numeric value from parsed args.
 * Tries named param first, falls back to positional index.
 * Returns undefined if not found or not parseable.
 */
function getNum(
  parsed: ParsedArgs,
  name: string,
  positionalIdx: number,
): number | undefined {
  const raw = parsed.byName.get(name) ?? parsed.positional[positionalIdx];
  if (raw === undefined) return undefined;
  const v = parseFloat(raw);
  return isNaN(v) ? undefined : v;
}

/**
 * Get a number, treating -1 as "not specified" (OpenSCAD sentinel).
 * Returns undefined for -1 or when not found.
 */
function getSentinel(
  parsed: ParsedArgs,
  name: string,
  positionalIdx: number,
): number | undefined {
  const v = getNum(parsed, name, positionalIdx);
  return v === undefined || v === -1 ? undefined : v;
}

/**
 * Get a boolean encoded as 0/1/-1 from parsed args.
 * Returns undefined for -1 (not specified).
 */
function getBoolSentinel(
  parsed: ParsedArgs,
  name: string,
  positionalIdx: number,
): boolean | undefined {
  const v = getNum(parsed, name, positionalIdx);
  return v === undefined || v === -1 ? undefined : v !== 0;
}

/**
 * Parse a SCAD vector literal [a, b, c] from a raw string.
 * Returns undefined if not parseable.
 */
function parseVec3(
  raw: string | undefined,
): [number, number, number] | undefined {
  if (!raw) return undefined;
  const m = raw
    .trim()
    .match(/^\[\s*([-\d.e+]+)\s*,\s*([-\d.e+]+)\s*,\s*([-\d.e+]+)\s*\]$/);
  if (!m) return undefined;
  return [parseFloat(m[1]), parseFloat(m[2]), parseFloat(m[3])];
}

/**
 * Get a vec3 from parsed args (named or positional).
 */
function getVec3(
  parsed: ParsedArgs,
  name: string,
  positionalIdx: number,
): [number, number, number] | undefined {
  const raw = parsed.byName.get(name) ?? parsed.positional[positionalIdx];
  return parseVec3(raw);
}

/**
 * Get a string value (strips surrounding quotes) from parsed args.
 */
function getString(
  parsed: ParsedArgs,
  name: string,
  positionalIdx: number,
): string | undefined {
  const raw = parsed.byName.get(name) ?? parsed.positional[positionalIdx];
  if (raw === undefined) return undefined;
  const t = raw.trim();
  if (t.startsWith('"') && t.endsWith('"')) return t.slice(1, -1);
  return t;
}

// ---------------------------------------------------------------------------
// LFO string parsers
// ---------------------------------------------------------------------------

const VALID_LFO_SHAPES: LFOShape[] = ['sine', 'triangle', 'square', 'sawtooth'];

function isValidLFOShape(s: string): s is LFOShape {
  return (VALID_LFO_SHAPES as string[]).includes(s);
}

/**
 * Parse an inline channel-LFO string: "shape:freq:phase:min:max"
 * Returns null if the string is empty, missing, or malformed.
 *
 * Example: "square:2:180:0:1"
 */
export function parseChannelLFO(raw: string | undefined): LightLFO | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;

  const parts = s.split(':');
  if (parts.length !== 5) return null;

  const shape = parts[0].trim();
  if (!isValidLFOShape(shape)) return null;

  const frequency = parseFloat(parts[1]);
  const phase = parseFloat(parts[2]);
  const min = parseFloat(parts[3]);
  const max = parseFloat(parts[4]);

  if ([frequency, phase, min, max].some(isNaN)) return null;

  return { shape, frequency, phase, min, max };
}

/**
 * Parse an inline color-LFO string: "shape:freq:phase:[r1,g1,b1]:[r2,g2,b2]"
 * Returns null if empty or malformed.
 *
 * Example: "sine:0.25:0:[1,0.1,0]:[0.1,0.2,1]"
 *
 * The two color vectors may contain spaces; we split on ':' carefully by
 * finding the first three colons and then taking the remainder as two
 * '[...]' groups.
 */
export function parseColorLFO(raw: string | undefined): LightColorLFO | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s) return null;

  // Find the first 3 colons to split shape, freq, phase; the rest is the two colors
  let colonCount = 0;
  let splitIdx = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === ':') {
      colonCount++;
      if (colonCount === 3) {
        splitIdx = i;
        break;
      }
    }
  }
  if (splitIdx === -1) return null;

  const prefix = s.slice(0, splitIdx);
  const colorsPart = s.slice(splitIdx + 1); // "[r1,g1,b1]:[r2,g2,b2]"

  const prefixParts = prefix.split(':');
  if (prefixParts.length !== 3) return null;

  const shape = prefixParts[0].trim();
  if (!isValidLFOShape(shape)) return null;

  const frequency = parseFloat(prefixParts[1]);
  const phase = parseFloat(prefixParts[2]);
  if (isNaN(frequency) || isNaN(phase)) return null;

  // Extract the two vec3 literals from the colors part
  const vecMatches = colorsPart.match(
    /^\s*(\[[\s\d.,eE+\-]+\])\s*:\s*(\[[\s\d.,eE+\-]+\])\s*$/,
  );
  if (!vecMatches) return null;

  const colorA = parseVec3(vecMatches[1]);
  const colorB = parseVec3(vecMatches[2]);
  if (!colorA || !colorB) return null;

  return { shape, frequency, phase, colorA, colorB };
}

/**
 * Build LFO bindings from the lfo_* string parameters parsed from a light call.
 * Returns undefined if no LFO params are set.
 */
function buildLFOBindings(params: {
  lfoIntensity: string | undefined;
  lfoColorR: string | undefined;
  lfoColorG: string | undefined;
  lfoColorB: string | undefined;
  lfoColor: string | undefined;
  lfoAngle: string | undefined;
  lfoPenumbra: string | undefined;
  lfoRotation?: string | undefined;
}): LightLFOBindings | undefined {
  const bindings: LightLFOBindings = {};

  const intensity = parseChannelLFO(params.lfoIntensity);
  if (intensity) bindings.intensity = intensity;

  const colorR = parseChannelLFO(params.lfoColorR);
  if (colorR) bindings.colorR = colorR;

  const colorG = parseChannelLFO(params.lfoColorG);
  if (colorG) bindings.colorG = colorG;

  const colorB = parseChannelLFO(params.lfoColorB);
  if (colorB) bindings.colorB = colorB;

  const color = parseColorLFO(params.lfoColor);
  if (color) bindings.color = color;

  const angle = parseChannelLFO(params.lfoAngle);
  if (angle) bindings.angle = angle;

  const penumbra = parseChannelLFO(params.lfoPenumbra);
  if (penumbra) bindings.penumbra = penumbra;

  const rotation = parseChannelLFO(params.lfoRotation);
  if (rotation) bindings.rotation = rotation;

  return Object.keys(bindings).length > 0 ? bindings : undefined;
}

// ---------------------------------------------------------------------------
// Default values matching WASM module definitions in openSCAD.ts
// ---------------------------------------------------------------------------

const DEFAULT_COLOR: [number, number, number] = [1, 1, 1];
const DEFAULT_POSITION: [number, number, number] = [0, 0, 0];
const DEFAULT_INTENSITY = 1;
const DEFAULT_DISTANCE = 0;
const DEFAULT_DECAY = 2;

// ---------------------------------------------------------------------------
// Light parsers
// ---------------------------------------------------------------------------

function parseLightAmbient(args: string): SceneLight | null {
  const parsed = parseArgs(args);
  const color = getVec3(parsed, 'color', 0) ?? DEFAULT_COLOR;
  const intensity = getNum(parsed, 'intensity', 1) ?? DEFAULT_INTENSITY;
  const lfo = buildLFOBindings({
    lfoIntensity: getString(parsed, 'lfo_intensity', 2),
    lfoColorR: getString(parsed, 'lfo_color_r', 3),
    lfoColorG: getString(parsed, 'lfo_color_g', 4),
    lfoColorB: getString(parsed, 'lfo_color_b', 5),
    lfoColor: getString(parsed, 'lfo_color', 6),
    lfoAngle: undefined,
    lfoPenumbra: undefined,
  });
  return { type: 'ambient', color, intensity, ...(lfo && { lfo }) };
}

function parseLightDirectional(args: string): SceneLight | null {
  const parsed = parseArgs(args);
  const color = getVec3(parsed, 'color', 0) ?? DEFAULT_COLOR;
  const position = getVec3(parsed, 'position', 1) ?? DEFAULT_POSITION;
  const intensity = getNum(parsed, 'intensity', 2) ?? DEFAULT_INTENSITY;
  const orbitRadius = getNum(parsed, 'orbit_radius', 3) ?? 0;
  const orbit =
    orbitRadius > 0
      ? {
          radius: orbitRadius,
          radiusB: getNum(parsed, 'orbit_radius_b', 4) ?? 0,
          tilt:
            getVec3(parsed, 'orbit_tilt', 5) ??
            ([0, 0, 0] as [number, number, number]),
          speed: getNum(parsed, 'orbit_speed', 6) ?? 0.3,
          driftSpeed: getNum(parsed, 'orbit_drift_speed', 7) ?? 0,
          driftAxis:
            getVec3(parsed, 'orbit_drift_axis', 8) ??
            ([0, 0, 1] as [number, number, number]),
        }
      : undefined;
  // LFO params come after the 9 orbit params (positional indices 9–14)
  const lfo = buildLFOBindings({
    lfoIntensity: getString(parsed, 'lfo_intensity', 9),
    lfoColorR: getString(parsed, 'lfo_color_r', 10),
    lfoColorG: getString(parsed, 'lfo_color_g', 11),
    lfoColorB: getString(parsed, 'lfo_color_b', 12),
    lfoColor: getString(parsed, 'lfo_color', 13),
    lfoAngle: undefined,
    lfoPenumbra: undefined,
  });
  return {
    type: 'directional',
    color,
    position,
    intensity,
    ...(orbit && { orbit }),
    ...(lfo && { lfo }),
  };
}

function parseLightPoint(args: string): SceneLight | null {
  const parsed = parseArgs(args);
  const color = getVec3(parsed, 'color', 0) ?? DEFAULT_COLOR;
  const position = getVec3(parsed, 'position', 1) ?? DEFAULT_POSITION;
  const intensity = getNum(parsed, 'intensity', 2) ?? DEFAULT_INTENSITY;
  const distance = getNum(parsed, 'distance', 3) ?? DEFAULT_DISTANCE;
  const decay = getNum(parsed, 'decay', 4) ?? DEFAULT_DECAY;
  const orbitRadius = getNum(parsed, 'orbit_radius', 5) ?? 0;
  const orbit =
    orbitRadius > 0
      ? {
          radius: orbitRadius,
          radiusB: getNum(parsed, 'orbit_radius_b', 6) ?? 0,
          tilt:
            getVec3(parsed, 'orbit_tilt', 7) ??
            ([0, 0, 0] as [number, number, number]),
          speed: getNum(parsed, 'orbit_speed', 8) ?? 0.3,
          driftSpeed: getNum(parsed, 'orbit_drift_speed', 9) ?? 0,
          driftAxis:
            getVec3(parsed, 'orbit_drift_axis', 10) ??
            ([0, 0, 1] as [number, number, number]),
        }
      : undefined;
  // LFO params come after the 11 orbit params (positional indices 11–16)
  const lfo = buildLFOBindings({
    lfoIntensity: getString(parsed, 'lfo_intensity', 11),
    lfoColorR: getString(parsed, 'lfo_color_r', 12),
    lfoColorG: getString(parsed, 'lfo_color_g', 13),
    lfoColorB: getString(parsed, 'lfo_color_b', 14),
    lfoColor: getString(parsed, 'lfo_color', 15),
    lfoAngle: undefined,
    lfoPenumbra: undefined,
  });
  return {
    type: 'point',
    color,
    position,
    intensity,
    distance,
    decay,
    ...(orbit && { orbit }),
    ...(lfo && { lfo }),
  };
}

function parseLightSpot(args: string): SceneLight | null {
  const parsed = parseArgs(args);
  const color = getVec3(parsed, 'color', 0) ?? DEFAULT_COLOR;
  const position = getVec3(parsed, 'position', 1) ?? DEFAULT_POSITION;
  const intensity = getNum(parsed, 'intensity', 2) ?? DEFAULT_INTENSITY;
  const distance = getNum(parsed, 'distance', 3) ?? DEFAULT_DISTANCE;
  const angle = getNum(parsed, 'angle', 4) ?? 60;
  const penumbra = getNum(parsed, 'penumbra', 5) ?? 0;
  const decay = getNum(parsed, 'decay', 6) ?? DEFAULT_DECAY;
  const orbitRadius = getNum(parsed, 'orbit_radius', 7) ?? 0;
  const orbit =
    orbitRadius > 0
      ? {
          radius: orbitRadius,
          radiusB: getNum(parsed, 'orbit_radius_b', 8) ?? 0,
          tilt:
            getVec3(parsed, 'orbit_tilt', 9) ??
            ([0, 0, 0] as [number, number, number]),
          speed: getNum(parsed, 'orbit_speed', 10) ?? 0.3,
          driftSpeed: getNum(parsed, 'orbit_drift_speed', 11) ?? 0,
          driftAxis:
            getVec3(parsed, 'orbit_drift_axis', 12) ??
            ([0, 0, 1] as [number, number, number]),
        }
      : undefined;
  // LFO params come after the 13 orbit params (positional indices 13–19)
  const lfo = buildLFOBindings({
    lfoIntensity: getString(parsed, 'lfo_intensity', 13),
    lfoColorR: getString(parsed, 'lfo_color_r', 14),
    lfoColorG: getString(parsed, 'lfo_color_g', 15),
    lfoColorB: getString(parsed, 'lfo_color_b', 16),
    lfoColor: getString(parsed, 'lfo_color', 17),
    lfoAngle: getString(parsed, 'lfo_angle', 18),
    lfoPenumbra: getString(parsed, 'lfo_penumbra', 19),
    lfoRotation: getString(parsed, 'lfo_rotation', 21),
  });
  // map param: filename of SVG/PNG to use as a gobo (positional index 20)
  const mapRaw = getString(parsed, 'map', 20);
  const map = mapRaw || undefined;
  return {
    type: 'spot',
    color,
    position,
    intensity,
    distance,
    angle,
    penumbra,
    decay,
    ...(map && { map }),
    ...(orbit && { orbit }),
    ...(lfo && { lfo }),
  };
}

// ---------------------------------------------------------------------------
// Scene override parsers
// ---------------------------------------------------------------------------

function parseSceneMaterial(args: string): SceneOverrides['material'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['material'] = {};

  // Positional order matches openSCAD.ts module definition:
  // metalness, roughness, env_map_intensity, flat_shading, use_physical,
  // clearcoat, clearcoat_roughness, sheen, sheen_roughness, sheen_color,
  // iridescence, iridescence_ior, transmission, ior, thickness,
  // attenuation_color, attenuation_distance

  const metalness = getSentinel(parsed, 'metalness', 0);
  const roughness = getSentinel(parsed, 'roughness', 1);
  const envMapIntensity = getSentinel(parsed, 'env_map_intensity', 2);
  const flatShading = getBoolSentinel(parsed, 'flat_shading', 3);
  const usePhysical = getBoolSentinel(parsed, 'use_physical', 4);
  const clearcoat = getSentinel(parsed, 'clearcoat', 5);
  const clearcoatRoughness = getSentinel(parsed, 'clearcoat_roughness', 6);
  const sheen = getSentinel(parsed, 'sheen', 7);
  const sheenRoughness = getSentinel(parsed, 'sheen_roughness', 8);
  const sheenColorRaw =
    parsed.byName.get('sheen_color') ?? parsed.positional[9];
  const sheenColor = parseVec3(sheenColorRaw);
  const iridescence = getSentinel(parsed, 'iridescence', 10);
  const iridescenceIOR = getSentinel(parsed, 'iridescence_ior', 11);
  const transmission = getSentinel(parsed, 'transmission', 12);
  const ior = getSentinel(parsed, 'ior', 13);
  const thickness = getSentinel(parsed, 'thickness', 14);
  const attenuationColorRaw =
    parsed.byName.get('attenuation_color') ?? parsed.positional[15];
  const attenuationColor = parseVec3(attenuationColorRaw);
  const attenuationDistance = getSentinel(parsed, 'attenuation_distance', 16);

  if (metalness !== undefined) result.metalness = metalness;
  if (roughness !== undefined) result.roughness = roughness;
  if (envMapIntensity !== undefined) result.envMapIntensity = envMapIntensity;
  if (flatShading !== undefined) result.flatShading = flatShading;
  if (usePhysical !== undefined) result.usePhysicalMaterial = usePhysical;
  if (clearcoat !== undefined) result.clearcoat = clearcoat;
  if (clearcoatRoughness !== undefined)
    result.clearcoatRoughness = clearcoatRoughness;
  if (sheen !== undefined) result.sheen = sheen;
  if (sheenRoughness !== undefined) result.sheenRoughness = sheenRoughness;
  if (
    sheenColor &&
    (sheenColor[0] !== -1 || sheenColor[1] !== -1 || sheenColor[2] !== -1)
  ) {
    result.sheenColor = [
      sheenColor[0] === -1 ? 1 : sheenColor[0],
      sheenColor[1] === -1 ? 1 : sheenColor[1],
      sheenColor[2] === -1 ? 1 : sheenColor[2],
    ];
  }
  if (iridescence !== undefined) result.iridescence = iridescence;
  if (iridescenceIOR !== undefined) result.iridescenceIOR = iridescenceIOR;
  if (transmission !== undefined) result.transmission = transmission;
  if (ior !== undefined) result.ior = ior;
  if (thickness !== undefined) result.thickness = thickness;
  if (
    attenuationColor &&
    (attenuationColor[0] !== -1 ||
      attenuationColor[1] !== -1 ||
      attenuationColor[2] !== -1)
  ) {
    result.attenuationColor = [
      attenuationColor[0] === -1 ? 1 : attenuationColor[0],
      attenuationColor[1] === -1 ? 1 : attenuationColor[1],
      attenuationColor[2] === -1 ? 1 : attenuationColor[2],
    ];
  }
  if (attenuationDistance !== undefined)
    result.attenuationDistance = attenuationDistance;

  return Object.keys(result).length > 0 ? result : null;
}

function parseSceneDefaultLights(
  args: string,
): SceneOverrides['defaultLights'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['defaultLights'] = {};

  const ambient = getSentinel(parsed, 'ambient', 0);
  const key = getSentinel(parsed, 'key', 1);
  const fill = getSentinel(parsed, 'fill', 2);
  const rim = getSentinel(parsed, 'rim', 3);

  if (ambient !== undefined) result.ambient = ambient;
  if (key !== undefined) result.key = key;
  if (fill !== undefined) result.fill = fill;
  if (rim !== undefined) result.rim = rim;

  return Object.keys(result).length > 0 ? result : null;
}

function parseSceneShadows(args: string): SceneOverrides['shadows'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['shadows'] = {};

  const enabled = getBoolSentinel(parsed, 'enabled', 0);
  const soft = getBoolSentinel(parsed, 'soft', 1);
  const intensity = getSentinel(parsed, 'intensity', 2);
  const mapSize = getSentinel(parsed, 'map_size', 3);

  if (enabled !== undefined) result.enabled = enabled;
  if (soft !== undefined) result.soft = soft;
  if (intensity !== undefined) result.intensity = intensity;
  if (mapSize !== undefined) result.mapSize = mapSize;

  return Object.keys(result).length > 0 ? result : null;
}

function parseSceneBloom(args: string): SceneOverrides['bloom'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['bloom'] = {};

  const enabled = getBoolSentinel(parsed, 'enabled', 0);
  const intensity = getSentinel(parsed, 'intensity', 1);
  const threshold = getSentinel(parsed, 'threshold', 2);
  const smoothing = getSentinel(parsed, 'smoothing', 3);
  const mipmapBlur = getBoolSentinel(parsed, 'mipmap_blur', 4);

  if (enabled !== undefined) result.enabled = enabled;
  if (intensity !== undefined) result.intensity = intensity;
  if (threshold !== undefined) result.luminanceThreshold = threshold;
  if (smoothing !== undefined) result.luminanceSmoothing = smoothing;
  if (mipmapBlur !== undefined) result.mipmapBlur = mipmapBlur;

  return Object.keys(result).length > 0 ? result : null;
}

function parseSceneSsao(args: string): SceneOverrides['ssao'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['ssao'] = {};

  const enabled = getBoolSentinel(parsed, 'enabled', 0);
  const intensity = getSentinel(parsed, 'intensity', 1);
  const radius = getSentinel(parsed, 'radius', 2);
  const samples = getSentinel(parsed, 'samples', 3);
  const luminanceInfluence = getSentinel(parsed, 'luminance_influence', 4);

  if (enabled !== undefined) result.enabled = enabled;
  if (intensity !== undefined) result.intensity = intensity;
  if (radius !== undefined) result.aoRadius = radius;
  if (samples !== undefined) result.samples = samples;
  if (luminanceInfluence !== undefined)
    result.luminanceInfluence = luminanceInfluence;

  return Object.keys(result).length > 0 ? result : null;
}

function parseSceneBackground(args: string): string | undefined {
  const parsed = parseArgs(args);
  const colorRaw = parsed.byName.get('color') ?? parsed.positional[0];
  const color = parseVec3(colorRaw);
  if (!color) return undefined;
  const [r, g, b] = color;
  if (r === -1 && g === -1 && b === -1) return undefined;
  const rr = r === -1 ? 0 : r;
  const gg = g === -1 ? 0 : g;
  const bb = b === -1 ? 0 : b;
  const toHex = (v: number) =>
    Math.round(Math.max(0, Math.min(1, v)) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(rr)}${toHex(gg)}${toHex(bb)}`;
}

function parseSceneGrid(args: string): SceneGrid | null {
  const parsed = parseArgs(args);

  // Positional order matches openSCAD.ts module definition:
  // type, size, position, rotation, color, opacity, cell_size, section_size,
  // cell_color, section_color, infinite, fade_distance
  const type = Math.round(getNum(parsed, 'type', 0) ?? 0) as 0 | 1 | 2;
  const size = getNum(parsed, 'size', 1) ?? 200;
  const position = getVec3(parsed, 'position', 2) ?? [0, 0, 0];
  const rotation = getVec3(parsed, 'rotation', 3) ?? [0, 0, 0];
  const color = getVec3(parsed, 'color', 4) ?? [0.5, 0.5, 0.5];
  const opacity = getNum(parsed, 'opacity', 5) ?? 0.5;
  const cellSize = getNum(parsed, 'cell_size', 6) ?? 1;
  const sectionSize = getNum(parsed, 'section_size', 7) ?? 10;

  const cellColorRaw = parsed.byName.get('cell_color') ?? parsed.positional[8];
  const cellColorParsed = parseVec3(cellColorRaw);
  const cellColor: [number, number, number] | undefined =
    cellColorParsed && cellColorParsed[0] !== -1 ? cellColorParsed : undefined;

  const sectionColorRaw =
    parsed.byName.get('section_color') ?? parsed.positional[9];
  const sectionColorParsed = parseVec3(sectionColorRaw);
  const sectionColor: [number, number, number] | undefined =
    sectionColorParsed && sectionColorParsed[0] !== -1
      ? sectionColorParsed
      : undefined;

  const infinite = (getNum(parsed, 'infinite', 10) ?? 0) !== 0;
  const fadeDistance = getNum(parsed, 'fade_distance', 11) ?? 200;

  return {
    type,
    size,
    position,
    rotation,
    color,
    opacity,
    cellSize,
    sectionSize,
    cellColor,
    sectionColor,
    infinite,
    fadeDistance,
  };
}

function parseSceneCamera(args: string): SceneOverrides['camera'] | null {
  const parsed = parseArgs(args);
  const result: SceneOverrides['camera'] = {};

  // Positional order: position, target, fov, type
  // Sentinel: [-1,-1,-1] for vec3, -1 for fov, "perspective" is default type.
  const posRaw = parsed.byName.get('position') ?? parsed.positional[0];
  const posParsed = parseVec3(posRaw);
  if (
    posParsed &&
    (posParsed[0] !== -1 || posParsed[1] !== -1 || posParsed[2] !== -1)
  ) {
    result.position = posParsed;
  }

  const tgtRaw = parsed.byName.get('target') ?? parsed.positional[1];
  const tgtParsed = parseVec3(tgtRaw);
  if (
    tgtParsed &&
    (tgtParsed[0] !== -1 || tgtParsed[1] !== -1 || tgtParsed[2] !== -1)
  ) {
    result.target = tgtParsed;
  }

  const fov = getSentinel(parsed, 'fov', 2);
  if (fov !== undefined) result.fov = fov;

  const typeRaw = getString(parsed, 'type', 3);
  if (typeRaw === 'orthographic') result.type = 'orthographic';
  else if (typeRaw === 'perspective') result.type = 'perspective';

  return Object.keys(result).length > 0 ? result : null;
}

function parseCameraPip(args: string): PiPCamera | null {
  const parsed = parseArgs(args);

  // Positional order: position, target, type, fov, x, y, width, height, label,
  //   transition, easing, duration, orbit_turns, orbit_axis,
  //   zoom_out_factor, roll_angle
  const position = getVec3(parsed, 'position', 0) ?? [50, 50, 50];
  const target = getVec3(parsed, 'target', 1) ?? [0, 0, 0];
  const typeRaw = getString(parsed, 'type', 2) ?? 'perspective';
  const type: 'perspective' | 'orthographic' =
    typeRaw === 'orthographic' ? 'orthographic' : 'perspective';
  const fov = getNum(parsed, 'fov', 3) ?? 45;
  const x = getNum(parsed, 'x', 4) ?? -1;
  const y = getNum(parsed, 'y', 5) ?? -1;
  const width = getNum(parsed, 'width', 6) ?? 0.3;
  const height = getNum(parsed, 'height', 7) ?? 0.25;
  // Pixel mode: |value| > 1 means the author specified a pixel value.
  const xPx = Math.abs(x) > 1 ? x : undefined;
  const yPx = Math.abs(y) > 1 ? y : undefined;
  const widthPx = width > 1 ? width : undefined;
  const heightPx = height > 1 ? height : undefined;
  // Use named-only lookup for label: positional index 8 is unreliable because
  // parseArgs also pushes named-arg values into the positional array, so
  // index 8 ends up pointing to whatever 9th named param appears in source.
  // label= is never passed positionally in practice.
  const labelRaw = parsed.byName.get('label');
  const label =
    labelRaw !== undefined
      ? labelRaw.startsWith('"') && labelRaw.endsWith('"')
        ? labelRaw.slice(1, -1)
        : labelRaw
      : '';

  // ── Transition parameters ─────────────────────────────────────────────────
  // "transitions" accepts a comma/plus-separated list, e.g. "orbit,roll,fov_pulse"
  const transitionsRaw = getString(parsed, 'transitions', 9) ?? 'direct';
  const validTransitions = [
    'direct',
    'orbit',
    'zoom_out',
    'arc',
    'roll',
    'tilt',
    'shake',
    'bounce',
    'fov_pulse',
  ] as const;
  type TransitionType = (typeof validTransitions)[number];
  const transitionParts = transitionsRaw
    .split(/[,+\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((p): p is TransitionType =>
      (validTransitions as readonly string[]).includes(p),
    );
  const withoutDirect = transitionParts.filter((t) => t !== 'direct');
  const transitions: TransitionType[] =
    withoutDirect.length > 0 ? withoutDirect : ['direct'];

  const easingRaw = getString(parsed, 'easing', 10) ?? 'exponential';
  const validEasings = [
    'exponential',
    'linear',
    'ease_in',
    'ease_out',
    'ease_in_out',
  ] as const;
  type EasingType = (typeof validEasings)[number];
  const easing: EasingType = (validEasings as readonly string[]).includes(
    easingRaw,
  )
    ? (easingRaw as EasingType)
    : 'exponential';

  const duration = getNum(parsed, 'duration', 11) ?? 1.0;
  const orbitTurns = getNum(parsed, 'orbit_turns', 12) ?? 1.0;

  const orbitAxisRaw = getString(parsed, 'orbit_axis', 13) ?? 'auto';
  const validOrbitAxes = ['auto', 'x', 'y', 'z'] as const;
  type OrbitAxisType = (typeof validOrbitAxes)[number];
  const orbitAxis: OrbitAxisType = (
    validOrbitAxes as readonly string[]
  ).includes(orbitAxisRaw)
    ? (orbitAxisRaw as OrbitAxisType)
    : 'auto';

  const zoomOutFactor = getNum(parsed, 'zoom_out_factor', 14) ?? 2.5;
  const rollAngle = getNum(parsed, 'roll_angle', 15) ?? 360;
  const arcHeight = getNum(parsed, 'arc_height', 16) ?? 30;
  const bounceAmplitude = getNum(parsed, 'bounce_amplitude', 17) ?? 0.3;
  const bounceFrequency = getNum(parsed, 'bounce_frequency', 18) ?? 2.0;
  const shakeIntensity = getNum(parsed, 'shake_intensity', 19) ?? 2.0;
  const shakeFrequency = getNum(parsed, 'shake_frequency', 20) ?? 8.0;

  const shakePhaseRaw = getString(parsed, 'shake_phase', 21) ?? 'end';
  const validShakePhases = ['start', 'end', 'both'] as const;
  type ShakePhaseType = (typeof validShakePhases)[number];
  const shakePhase: ShakePhaseType = (
    validShakePhases as readonly string[]
  ).includes(shakePhaseRaw)
    ? (shakePhaseRaw as ShakePhaseType)
    : 'end';

  const tiltAngle = getNum(parsed, 'tilt_angle', 22) ?? 30;
  const fovPulseAmount = getNum(parsed, 'fov_pulse_amount', 23) ?? 20;

  const fovPulseDirRaw = getString(parsed, 'fov_pulse_direction', 24) ?? 'out';
  const fovPulseDirection: 'in' | 'out' =
    fovPulseDirRaw === 'in' ? 'in' : 'out';

  // Clamp randomness to [0, 1]
  const randomness = Math.max(
    0,
    Math.min(1, getNum(parsed, 'randomness', 25) ?? 0),
  );

  return {
    position,
    target,
    type,
    fov,
    x,
    y,
    width,
    height,
    ...(xPx !== undefined && { xPx }),
    ...(yPx !== undefined && { yPx }),
    ...(widthPx !== undefined && { widthPx }),
    ...(heightPx !== undefined && { heightPx }),
    label,
    transitions,
    easing,
    duration,
    orbitTurns,
    orbitAxis,
    zoomOutFactor,
    rollAngle,
    arcHeight,
    bounceAmplitude,
    bounceFrequency,
    shakeIntensity,
    shakeFrequency,
    shakePhase,
    tiltAngle,
    fovPulseAmount,
    fovPulseDirection,
    randomness,
  };
}

// ---------------------------------------------------------------------------
// camera_path() parser
// ---------------------------------------------------------------------------

/**
 * Parse a camera_path() module call and return a CameraPathWaypoint.
 *
 * Signature mirrors camera_pip() but without overlay layout fields (x/y/width/height/label).
 * Positional order: position, target, type, fov, transitions, easing, duration,
 *   orbit_turns, orbit_axis, zoom_out_factor, roll_angle, arc_height,
 *   bounce_amplitude, bounce_frequency, shake_intensity, shake_frequency, shake_phase,
 *   tilt_angle, fov_pulse_amount, fov_pulse_direction, randomness
 */
function parseCameraPath(args: string): CameraPathWaypoint | null {
  const parsed = parseArgs(args);

  const position = getVec3(parsed, 'position', 0) ?? [50, 50, 50];
  const target = getVec3(parsed, 'target', 1) ?? [0, 0, 0];
  const typeRaw = getString(parsed, 'type', 2) ?? 'perspective';
  const type: 'perspective' | 'orthographic' =
    typeRaw === 'orthographic' ? 'orthographic' : 'perspective';
  const fov = getNum(parsed, 'fov', 3) ?? 45;

  // ── Transition parameters ─────────────────────────────────────────────────
  const transitionsRaw = getString(parsed, 'transitions', 4) ?? 'direct';
  const validTransitions = [
    'direct',
    'orbit',
    'zoom_out',
    'arc',
    'roll',
    'tilt',
    'shake',
    'bounce',
    'fov_pulse',
  ] as const;
  type TransitionType = (typeof validTransitions)[number];
  const transitionParts = transitionsRaw
    .split(/[,+\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((p): p is TransitionType =>
      (validTransitions as readonly string[]).includes(p),
    );
  const withoutDirect = transitionParts.filter((t) => t !== 'direct');
  const transitions: TransitionType[] =
    withoutDirect.length > 0 ? withoutDirect : ['direct'];

  const easingRaw = getString(parsed, 'easing', 5) ?? 'exponential';
  const validEasings = [
    'exponential',
    'linear',
    'ease_in',
    'ease_out',
    'ease_in_out',
  ] as const;
  type EasingType = (typeof validEasings)[number];
  const easing: EasingType = (validEasings as readonly string[]).includes(
    easingRaw,
  )
    ? (easingRaw as EasingType)
    : 'exponential';

  const duration = getNum(parsed, 'duration', 6) ?? 1.0;
  const orbitTurns = getNum(parsed, 'orbit_turns', 7) ?? 1.0;

  const orbitAxisRaw = getString(parsed, 'orbit_axis', 8) ?? 'auto';
  const validOrbitAxes = ['auto', 'x', 'y', 'z'] as const;
  type OrbitAxisType = (typeof validOrbitAxes)[number];
  const orbitAxis: OrbitAxisType = (
    validOrbitAxes as readonly string[]
  ).includes(orbitAxisRaw)
    ? (orbitAxisRaw as OrbitAxisType)
    : 'auto';

  const zoomOutFactor = getNum(parsed, 'zoom_out_factor', 9) ?? 2.5;
  const rollAngle = getNum(parsed, 'roll_angle', 10) ?? 360;
  const arcHeight = getNum(parsed, 'arc_height', 11) ?? 30;
  const bounceAmplitude = getNum(parsed, 'bounce_amplitude', 12) ?? 0.3;
  const bounceFrequency = getNum(parsed, 'bounce_frequency', 13) ?? 2.0;
  const shakeIntensity = getNum(parsed, 'shake_intensity', 14) ?? 2.0;
  const shakeFrequency = getNum(parsed, 'shake_frequency', 15) ?? 8.0;

  const shakePhaseRaw = getString(parsed, 'shake_phase', 16) ?? 'end';
  const shakePhase: 'start' | 'end' | 'both' =
    shakePhaseRaw === 'start' || shakePhaseRaw === 'both'
      ? shakePhaseRaw
      : 'end';

  const tiltAngle = getNum(parsed, 'tilt_angle', 17) ?? 30;
  const fovPulseAmount = getNum(parsed, 'fov_pulse_amount', 18) ?? 20;

  const fovPulseDirRaw = getString(parsed, 'fov_pulse_direction', 19) ?? 'out';
  const fovPulseDirection: 'in' | 'out' =
    fovPulseDirRaw === 'in' ? 'in' : 'out';

  const randomness = getNum(parsed, 'randomness', 20) ?? 0;

  return {
    position,
    target,
    type,
    fov,
    transitions,
    easing,
    duration,
    orbitTurns,
    orbitAxis,
    zoomOutFactor,
    rollAngle,
    arcHeight,
    bounceAmplitude,
    bounceFrequency,
    shakeIntensity,
    shakeFrequency,
    shakePhase,
    tiltAngle,
    fovPulseAmount,
    fovPulseDirection,
    randomness,
  };
}

/**
 * Parse a svg_overlay() module call and return an SvgOverlay.
 * Signature: svg_overlay(file="", x=0.02, y=0.02, width=0.25, height=0, color="", opacity=1.0)
 *
 * Position/size follow the same fraction/pixel convention as camera_pip():
 *   |value| ≤ 1 = fraction of viewer dimension
 *   |value| > 1 = pixels (positive from left/top, negative from right/bottom)
 * height=0 means "derive from SVG aspect ratio".
 */
function parseSvgOverlay(args: string): SvgOverlay | null {
  const parsed = parseArgs(args);

  // Positional order: file, x, y, width, height, color, opacity
  const file = getString(parsed, 'file', 0) ?? '';
  if (!file) return null; // file is required

  const x = getNum(parsed, 'x', 1) ?? 0.02;
  const y = getNum(parsed, 'y', 2) ?? 0.02;
  const width = getNum(parsed, 'width', 3) ?? 0.25;
  const height = getNum(parsed, 'height', 4) ?? 0;

  // color is a CSS string; strip surrounding quotes if present
  const colorRaw = parsed.byName.get('color') ?? parsed.positional[5];
  const color =
    colorRaw !== undefined
      ? colorRaw.startsWith('"') && colorRaw.endsWith('"')
        ? colorRaw.slice(1, -1)
        : colorRaw
      : '';

  const opacity = getNum(parsed, 'opacity', 6) ?? 1.0;

  // Pixel mode: |value| > 1 means the author specified a pixel value.
  const xPx = Math.abs(x) > 1 ? x : undefined;
  const yPx = Math.abs(y) > 1 ? y : undefined;
  const widthPx = width > 1 ? width : undefined;
  // height=0 is the AR sentinel — only set heightPx when height is an explicit pixel value
  const heightPx = height > 1 ? height : undefined;

  return {
    file,
    x,
    y,
    width,
    height,
    ...(xPx !== undefined && { xPx }),
    ...(yPx !== undefined && { yPx }),
    ...(widthPx !== undefined && { widthPx }),
    ...(heightPx !== undefined && { heightPx }),
    color,
    opacity,
  };
}

// ---------------------------------------------------------------------------
// annotation() parser
// ---------------------------------------------------------------------------

/**
 * Parse an annotation() module call and return the AnnotationDef metadata.
 * Signature: annotation(name="", description="", alwaysVisible=false)
 *
 * Note: geometry association is not possible here — WASM is authoritative
 * for that. This parser is only used to detect that annotations exist.
 */
function parseAnnotation(args: string): AnnotationDef | null {
  const parsed = parseArgs(args);

  // Positional order: name, description, alwaysVisible
  const name = getString(parsed, 'name', 0) ?? '';
  const description = getString(parsed, 'description', 1) ?? '';
  // alwaysVisible is a boolean; in SCAD it may be true/false or 0/1
  const alwaysVisibleRaw =
    parsed.byName.get('alwaysVisible') ?? parsed.positional[2];
  let alwaysVisible = false;
  if (alwaysVisibleRaw !== undefined) {
    const t = alwaysVisibleRaw.trim();
    alwaysVisible = t === 'true' || t === '1';
  }

  return { name, description, alwaysVisible };
}

// ---------------------------------------------------------------------------
// motion() parser
// ---------------------------------------------------------------------------

/**
 * Default values matching the motion() module defaults in openSCAD.ts stubs.
 */
const DEFAULT_TRANSLATE_AXIS: [number, number, number] = [0, 1, 0];
const DEFAULT_ORBIT_CENTER: [number, number, number] = [0, 0, 0];
const DEFAULT_ORBIT_AXIS: [number, number, number] = [0, 0, 1];
const DEFAULT_ROTATE_AXIS: [number, number, number] = [0, 0, 1];
const DEFAULT_ROCK_AXIS: [number, number, number] = [1, 0, 0];

/**
 * Parse a motion() module call and return a MotionDef.
 *
 * Signature:
 *   motion(
 *     translate_axis=[0,1,0], translate_lfo="",
 *     orbit_center=[0,0,0], orbit_axis=[0,0,1],
 *     orbit_radius=0, orbit_radius_b=0, orbit_speed=0.5, orbit_offset=0,
 *     rotate_axis=[0,0,1], rotate_speed=0, rotate_offset=0,
 *     rock_axis=[1,0,0], rock_lfo=""
 *   )
 *
 * This parser is used by the instant JS parse path to detect motion() presence.
 * Full MotionDef construction for Three.js animation happens in the preprocessor +
 * WASM detect pipeline.
 */
export function parseMotion(args: string): MotionDef | null {
  const parsed = parseArgs(args);

  const translateAxis =
    getVec3(parsed, 'translate_axis', 0) ?? DEFAULT_TRANSLATE_AXIS;
  const translateLfoStr = getString(parsed, 'translate_lfo', 1);
  const translateLFO = parseChannelLFO(translateLfoStr) ?? undefined;

  const orbitCenter =
    getVec3(parsed, 'orbit_center', 2) ?? DEFAULT_ORBIT_CENTER;
  const orbitAxis = getVec3(parsed, 'orbit_axis', 3) ?? DEFAULT_ORBIT_AXIS;
  const orbitRadius = getNum(parsed, 'orbit_radius', 4) ?? 0;
  const orbitRadiusB = getNum(parsed, 'orbit_radius_b', 5) ?? 0;
  const orbitSpeed = getNum(parsed, 'orbit_speed', 6) ?? 0.5;
  const orbitOffset = getNum(parsed, 'orbit_offset', 7) ?? 0;

  const rotateAxis = getVec3(parsed, 'rotate_axis', 8) ?? DEFAULT_ROTATE_AXIS;
  const rotateSpeed = getNum(parsed, 'rotate_speed', 9) ?? 0;
  const rotateOffset = getNum(parsed, 'rotate_offset', 10) ?? 0;

  const rockAxis = getVec3(parsed, 'rock_axis', 11) ?? DEFAULT_ROCK_AXIS;
  const rockLfoStr = getString(parsed, 'rock_lfo', 12);
  const rockLFO = parseChannelLFO(rockLfoStr) ?? undefined;

  return {
    translateAxis,
    ...(translateLFO && { translateLFO }),
    orbitCenter,
    orbitAxis,
    orbitRadius,
    orbitRadiusB,
    orbitSpeed,
    orbitOffset,
    rotateAxis,
    rotateSpeed,
    rotateOffset,
    rockAxis,
    ...(rockLFO && { rockLFO }),
  };
}
